import produce from 'immer';
import type { ICampaignApplicationReviewData } from './campaignApplicationReview.types';
import { threadMessageToWorkspaceMessage } from './campaignThreadMessage.utils';
import type {
    ICampaignThreadAttachmentProcessingPayload,
    ICampaignThreadMessage,
    ICampaignThreadTypingState,
} from './campaignThreadMessage.types';
import type { ICampaignWorkspaceData, IWorkspaceMessage } from './campaignWorkspace.types';
import {
    applyAllThreadsReadToCampaign,
    applyThreadReadToCampaign,
    applyThreadReadToInfluencerProfile,
    setParticipantUnreadOnCampaign,
    setParticipantUnreadOnInfluencerProfile,
} from '../../utils/campaignThreadUnread';
import type {
    ICampaignReportContentResponse,
    ICampaignReportCreatorsResponse,
    ICampaignReportSummary,
    ICampaignReportSyncStatusResponse,
} from '../../types/global/campaignReport.types';
import type { ICommissionWalletSummary } from '../../types/global/commissionWallet.types';
import {
    IBrand,
    IBuzzBridgeProfileResponse,
    ICampaignParticipantDetail,
    IEscrow,
    IHostDashboardData,
    IInfluencer,
    IWalletDashboard,
    IWalletFundOptions,
    IWalletTransaction,
    IAdminCreditWalletResult,
    types,
} from './types';
import { types as uploadTypes } from '../upload/types';
import { REHYDRATE } from 'redux-persist';

function upsertWorkspaceThreadMessage(ws: ICampaignWorkspaceData, message: ICampaignThreadMessage, appendIfNew = true) {
    const normalized = threadMessageToWorkspaceMessage(message);
    const id = normalized._id;
    if (!ws.messages) ws.messages = [];
    const idx = id ? ws.messages.findIndex(m => m._id === id) : -1;
    if (idx >= 0) {
        ws.messages[idx] = normalized;
        return;
    }
    if (appendIfNew) {
        ws.messages = [...ws.messages, normalized];
        if (ws.messages_pagination) {
            ws.messages_pagination.total = (ws.messages_pagination.total ?? 0) + 1;
        }
    }
}

function applyAttachmentProcessingUpdate(
    ws: ICampaignWorkspaceData,
    payload: ICampaignThreadAttachmentProcessingPayload,
) {
    const msg = ws.messages?.find(m => m._id === payload.messageId);
    if (!msg?.attachments?.length) return;
    msg.attachments = msg.attachments.map(att => {
        if (att.id !== payload.attachmentId) return att;
        return {
            ...att,
            processing_progress: payload.processing_progress ?? att.processing_progress,
            processing_complete:
                payload.processing_complete !== undefined ? payload.processing_complete : att.processing_complete,
            processing_error: payload.processing_error ?? att.processing_error,
        };
    });
}

// Interface for BuzzBridge state
export interface IBuzzBridgeInitialState {
    buzzProfile?: IBuzzBridgeProfileResponse | null;
    gettingBuzzProfile?: boolean;
    // Program state
    joiningProgram: boolean;
    programJoined: boolean;
    programError: any;

    // Campaigns state
    campaigns: any[];
    currentCampaign: any;
    lastCreatedCampaignId: string | null;
    creatingCampaign: boolean;
    updatingCampaign: boolean;
    updatingCampaignStatus: boolean;
    deletingCampaign: boolean;
    gettingCampaignDetails: boolean;
    campaignError: any;

    // Campaign jobs state
    campaignJobs: any[];
    gettingCampaignJobs: boolean;
    campaignJobsError: any;

    // Managed campaigns state
    managedCampaigns: any[];
    managedCampaignsPagination: { currentPage: number; totalPages: number; totalCount: number };
    gettingManagedCampaigns: boolean;
    managedCampaignsError: any;

    // Brand management state
    brands: IBrand[];
    /** Active brand for brand dashboard (header, scoped flows). */
    activeBrandDashboardBrand: IBrand | null;
    gettingBrands: boolean;
    addingBrand: boolean;
    updatingBrand: boolean;
    deletingBrand: boolean;
    brandError: any;

    // Host dashboard state
    hostDashboard: IHostDashboardData | null;
    gettingHostDashboard: boolean;
    hostDashboardError: any;

    // Join requests state
    joinRequests: any[];
    requestingToJoin: boolean;
    approvingRequest: boolean;
    bulkApprovingRequests: boolean;
    bulkRejectingRequests: boolean;
    bulkRescindingParticipants: boolean;
    bulkRescindLastSkipped: { id: string; reason: string }[];
    rejectingRequest: boolean;
    joinRequestError: any;
    /** Paginated host pending invites (GET …/pending-invites) */
    pendingInvitesList: ICampaignParticipantDetail[];
    pendingInvitesPagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
        hasMore: boolean;
    };
    gettingPendingInvites: boolean;
    cancellingInvite: boolean;

    // Messages state
    messages: any[];
    sendingMessage: boolean;
    deletingMessage: boolean;
    updatingMessage: boolean;
    messageError: any;

    // Deliverables state
    deliverables: any[];
    requestingUpdate: boolean;
    bulkRequestingRevision: boolean;
    relaunchingCampaign: boolean;
    relaunchedCampaignId: string | null;
    approvingDeliverable: boolean;
    uploadingDeliverable: boolean;
    deliverableError: any;

    // Post scheduling state
    scheduledPosts: any[];
    schedulingPost: boolean;
    scheduleError: any;

    // Brand social OAuth
    connectingBrandSocial: boolean;

    // Campaign Wallet
    walletDashboard: IWalletDashboard | null;
    gettingWalletDashboard: boolean;
    walletFundOptions: IWalletFundOptions | null;
    gettingWalletFundOptions: boolean;
    fundingWallet: boolean;
    walletTransactions: IWalletTransaction[];
    gettingWalletTransactions: boolean;
    walletError: any;
    adminCreditingCampaignWallet: boolean;
    adminCampaignWalletCreditResult: IAdminCreditWalletResult | null;

    /** Influencer commission wallet (separate from campaign escrow wallet) */
    commissionWallet: ICommissionWalletSummary | null;
    gettingCommissionWallet: boolean;
    commissionWalletError: any;
    cashingOutCommissionWallet: boolean;

    // General loading states
    loading: boolean;
    error: any;

    /** True after GET /me/profile 404 — show creator onboarding */
    noInfluencerProfile?: boolean;

    /** GET /influencers/public/:userName */
    publicInfluencerProfile: IInfluencer | null;
    publicInfluencerProfileUserName: string | null;
    gettingPublicInfluencerProfile: boolean;
    publicInfluencerProfileError: unknown;

    /** Brand: GET /campaign-participants/:id/application-review */
    campaignApplicationReview: ICampaignApplicationReviewData | null;
    gettingCampaignApplicationReview: boolean;
    campaignApplicationReviewError: unknown;

    /** Brand / creator: GET .../brand-workspace | creator-workspace */
    campaignWorkspace: ICampaignWorkspaceData | null;
    gettingCampaignWorkspace: boolean;
    loadingOlderWorkspaceMessages: boolean;
    campaignWorkspaceError: unknown;

    /** Campaign participant thread (campaignthreadmessages collection) */
    sendingCampaignThreadMessage: boolean;
    gettingCampaignThreadMessages: boolean;
    loadingOlderCampaignThreadMessages: boolean;
    patchingCampaignThreadMessage: boolean;
    deletingCampaignThreadMessage: boolean;
    campaignThreadMessageError: unknown;
    /** Remote typing indicator keyed by participantId */
    campaignThreadTyping: Record<string, ICampaignThreadTypingState | null>;
    /** Last typing pulse timestamp (ms) — used to extend indicator while heartbeats arrive. */
    campaignThreadTypingAt: Record<string, number>;
    /** Thread rooms the client should stay joined to (for socket replay after reconnect). */
    campaignThreadActiveRooms: Record<string, true>;

    submittingParticipantWork: boolean;
    participantWorkSubmitError: unknown;
    participantUploadProgress: number;

    retryingSubmissionProcessing: boolean;
    withdrawingFromCampaign: boolean;
    requestingDeliveryExtension: boolean;
    reviewingDeliveryExtension: boolean;
    openingLiveLinkWindow: boolean;

    submittingLiveUrl: boolean;
    liveUrlSubmitError: unknown;

    markingShared: boolean;
    markSharedError: unknown;

    requestingLiveUrlRevision: boolean;
    liveUrlRevisionRequestError: unknown;

    savingBrandReview: boolean;
    brandReviewSaveError: unknown;

    savingInfluencerReview: boolean;
    influencerReviewSaveError: unknown;

    payingOut: boolean;
    payoutError: unknown;
    payingOutRound: boolean;

    /** Brand: GET /campaigns/:id/report* */
    campaignReportSummary: ICampaignReportSummary | null;
    campaignReportContent: ICampaignReportContentResponse | null;
    campaignReportCreators: ICampaignReportCreatorsResponse | null;
    campaignReportSyncStatus: ICampaignReportSyncStatusResponse | null;
    gettingCampaignReport: boolean;
    gettingCampaignReportContent: boolean;
    gettingCampaignReportCreators: boolean;
    gettingCampaignReportSyncStatus: boolean;
    syncingCampaignReport: boolean;
    boostingCampaignReport: boolean;
    campaignReportBoostResult: import('../../types/global/campaignReport.types').IBoostCampaignReportResult | null;
    campaignReportError: unknown;
}

const initialState: IBuzzBridgeInitialState = {
    // currentUserProfile
    buzzProfile: null,
    // Program state
    joiningProgram: false,
    programJoined: false,
    programError: null,

    // Campaigns state
    campaigns: [],
    currentCampaign: null,
    lastCreatedCampaignId: null,
    creatingCampaign: false,
    updatingCampaign: false,
    updatingCampaignStatus: false,
    deletingCampaign: false,
    gettingCampaignDetails: false,
    campaignError: null,

    // Campaign jobs state
    campaignJobs: [],
    gettingCampaignJobs: false,
    campaignJobsError: null,

    // Managed campaigns state
    managedCampaigns: [],
    managedCampaignsPagination: { currentPage: 1, totalPages: 1, totalCount: 0 },
    gettingManagedCampaigns: false,
    managedCampaignsError: null,

    // Brand management state
    brands: [],
    activeBrandDashboardBrand: null,
    gettingBrands: false,
    addingBrand: false,
    updatingBrand: false,
    deletingBrand: false,
    brandError: null,

    // Host dashboard state
    hostDashboard: null,
    gettingHostDashboard: false,
    hostDashboardError: null,

    // Join requests state
    joinRequests: [],
    requestingToJoin: false,
    approvingRequest: false,
    bulkApprovingRequests: false,
    bulkRejectingRequests: false,
    bulkRescindingParticipants: false,
    bulkRescindLastSkipped: [],
    rejectingRequest: false,
    joinRequestError: null,
    pendingInvitesList: [],
    pendingInvitesPagination: { page: 1, limit: 20, total: 0, totalPages: 1, hasMore: false },
    gettingPendingInvites: false,
    cancellingInvite: false,

    // Messages state
    messages: [],
    sendingMessage: false,
    deletingMessage: false,
    updatingMessage: false,
    messageError: null,

    // Deliverables state
    deliverables: [],
    requestingUpdate: false,
    bulkRequestingRevision: false,
    relaunchingCampaign: false,
    relaunchedCampaignId: null,
    approvingDeliverable: false,
    uploadingDeliverable: false,
    deliverableError: null,

    // Post scheduling state
    scheduledPosts: [],
    schedulingPost: false,
    scheduleError: null,

    // Brand social OAuth
    connectingBrandSocial: false,

    // Campaign Wallet
    walletDashboard: null,
    gettingWalletDashboard: false,
    walletFundOptions: null,
    gettingWalletFundOptions: false,
    fundingWallet: false,
    walletTransactions: [],
    gettingWalletTransactions: false,
    walletError: null,
    adminCreditingCampaignWallet: false,
    adminCampaignWalletCreditResult: null,

    commissionWallet: null,
    gettingCommissionWallet: false,
    commissionWalletError: null,
    cashingOutCommissionWallet: false,

    // General loading states
    loading: false,
    error: null,

    noInfluencerProfile: false,

    publicInfluencerProfile: null,
    publicInfluencerProfileUserName: null,
    gettingPublicInfluencerProfile: false,
    publicInfluencerProfileError: null,

    campaignApplicationReview: null,
    gettingCampaignApplicationReview: false,
    campaignApplicationReviewError: null,

    campaignWorkspace: null,
    gettingCampaignWorkspace: false,
    loadingOlderWorkspaceMessages: false,
    campaignWorkspaceError: null,

    sendingCampaignThreadMessage: false,
    gettingCampaignThreadMessages: false,
    loadingOlderCampaignThreadMessages: false,
    patchingCampaignThreadMessage: false,
    deletingCampaignThreadMessage: false,
    campaignThreadMessageError: null,
    campaignThreadTyping: {},
    campaignThreadTypingAt: {},
    campaignThreadActiveRooms: {},

    submittingParticipantWork: false,
    participantWorkSubmitError: null,
    participantUploadProgress: 0,

    retryingSubmissionProcessing: false,
    withdrawingFromCampaign: false,
    requestingDeliveryExtension: false,
    reviewingDeliveryExtension: false,
    openingLiveLinkWindow: false,

    submittingLiveUrl: false,
    liveUrlSubmitError: null,

    markingShared: false,
    markSharedError: null,

    requestingLiveUrlRevision: false,
    liveUrlRevisionRequestError: null,

    savingBrandReview: false,
    brandReviewSaveError: null,

    savingInfluencerReview: false,
    influencerReviewSaveError: null,

    payingOut: false,
    payoutError: null,
    payingOutRound: false,

    campaignReportSummary: null,
    campaignReportContent: null,
    campaignReportCreators: null,
    campaignReportSyncStatus: null,
    gettingCampaignReport: false,
    gettingCampaignReportContent: false,
    gettingCampaignReportCreators: false,
    gettingCampaignReportSyncStatus: false,
    syncingCampaignReport: false,
    boostingCampaignReport: false,
    campaignReportBoostResult: null,
    campaignReportError: null,
};

const reducer = (state = initialState, action: any) => {
    const { type, payload } = action;
    switch (type) {
        case REHYDRATE:
            return {
                ...state,
                ...(payload?.buzzbridge ?? {}),
                participantUploadProgress: 0,
                submittingParticipantWork: false,
            };
        // Program actions
        case types.JOIN_PROGRAM:
            return produce(state, draftState => {
                draftState.joiningProgram = true;
                draftState.programError = null;
            });
        case types.JOIN_PROGRAM_SUCCESS:
            return produce(state, draftState => {
                draftState.joiningProgram = false;
                draftState.programJoined = true;
            });
        case types.JOIN_PROGRAM_FAIL:
            return produce(state, draftState => {
                draftState.joiningProgram = false;
                draftState.programError = payload;
            });

        // Campaign actions
        case types.CREATE_OFFSITE_CAMPAIGN:
            return produce(state, draftState => {
                draftState.creatingCampaign = true;
                draftState.campaignError = null;
            });
        case types.CREATE_OFFSITE_CAMPAIGN_SUCCESS:
            return produce(state, draftState => {
                draftState.creatingCampaign = false;
                draftState.lastCreatedCampaignId = payload?._id ?? null;
                draftState.campaigns = [...draftState.campaigns, payload];
            });
        case types.CREATE_OFFSITE_CAMPAIGN_FAIL:
            return produce(state, draftState => {
                draftState.creatingCampaign = false;
                draftState.campaignError = payload;
            });
        case types.RESET_CREATING_CAMPAIGN:
            return produce(state, draftState => {
                draftState.creatingCampaign = false;
            });

        case types.GET_OFFSITE_CAMPAIGN_DETAILS:
            return produce(state, draftState => {
                draftState.gettingCampaignDetails = true;
                draftState.campaignError = null;
            });
        case types.GET_OFFSITE_CAMPAIGN_DETAILS_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingCampaignDetails = false;
                const prev = draftState.currentCampaign;
                const p = payload;
                const sameId = prev && String(prev._id || prev.id) === String(p._id || p.id);
                // Merge so fields omitted by the API (e.g. `funded`) do not wipe optimistic updates after escrow lock
                draftState.currentCampaign = sameId ? { ...prev, ...p } : p;
            });
        case types.GET_OFFSITE_CAMPAIGN_DETAILS_FAIL:
            return produce(state, draftState => {
                draftState.gettingCampaignDetails = false;
                draftState.campaignError = payload;
            });

        case types.GET_CAMPAIGN_JOB_DETAILS:
            return produce(state, draftState => {
                draftState.gettingCampaignJobs = true;
                draftState.campaignJobsError = null;
            });
        case types.GET_CAMPAIGN_JOB_DETAILS_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingCampaignJobs = false;
                draftState.campaignJobs = payload;
            });
        case types.GET_CAMPAIGN_JOB_DETAILS_FAIL:
            return produce(state, draftState => {
                draftState.gettingCampaignJobs = false;
                draftState.campaignJobsError = payload;
            });

        case types.GET_CAMPAIGN_APPLICATION_REVIEW:
            return produce(state, draftState => {
                draftState.gettingCampaignApplicationReview = true;
                draftState.campaignApplicationReviewError = null;
                draftState.campaignApplicationReview = null;
            });
        case types.GET_CAMPAIGN_APPLICATION_REVIEW_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingCampaignApplicationReview = false;
                draftState.campaignApplicationReview = payload;
            });
        case types.GET_CAMPAIGN_APPLICATION_REVIEW_FAIL:
            return produce(state, draftState => {
                draftState.gettingCampaignApplicationReview = false;
                draftState.campaignApplicationReviewError = payload;
            });
        case types.CLEAR_CAMPAIGN_APPLICATION_REVIEW:
            return produce(state, draftState => {
                draftState.campaignApplicationReview = null;
                draftState.campaignApplicationReviewError = null;
                draftState.gettingCampaignApplicationReview = false;
            });

        case types.GET_CAMPAIGN_WORKSPACE: {
            const append = Boolean(payload?.appendMessages);
            return produce(state, draftState => {
                draftState.campaignWorkspaceError = null;
                if (append) {
                    draftState.loadingOlderWorkspaceMessages = true;
                } else {
                    draftState.gettingCampaignWorkspace = true;
                    // Keep existing workspace mounted so UI state (e.g. selected
                    // submission tab) survives approve / revise refreshes.
                    // Full clear still happens via CLEAR_CAMPAIGN_WORKSPACE on navigate.
                }
            });
        }
        case types.GET_CAMPAIGN_WORKSPACE_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingCampaignWorkspace = false;
                draftState.loadingOlderWorkspaceMessages = false;
                // Reset any in-flight submission flags so stale spinners don't persist
                // across navigation or after a brand action reloads the workspace.
                draftState.submittingLiveUrl = false;
                draftState.markingShared = false;
                draftState.requestingLiveUrlRevision = false;
                // Reset any in-flight submission flags so stale spinners don't persist
                // across navigation or after a brand action reloads the workspace.
                draftState.submittingLiveUrl = false;
                draftState.markingShared = false;
                draftState.requestingLiveUrlRevision = false;
                const { data, append } = payload;
                if (append && draftState.campaignWorkspace) {
                    const existing = draftState.campaignWorkspace.messages ?? [];
                    const incoming = data.messages ?? [];
                    const seen = new Set(existing.map(m => m._id).filter(Boolean) as string[]);
                    const prepended = incoming.filter((m: { _id: any }) => {
                        const id = m._id;
                        return !id || !seen.has(id);
                    });
                    draftState.campaignWorkspace = {
                        ...draftState.campaignWorkspace,
                        messages: [...prepended, ...existing],
                        messages_pagination: data.messages_pagination,
                    };
                } else {
                    draftState.campaignWorkspace = data;
                }
            });
        case types.GET_CAMPAIGN_WORKSPACE_FAIL:
            return produce(state, draftState => {
                draftState.gettingCampaignWorkspace = false;
                draftState.loadingOlderWorkspaceMessages = false;
                draftState.campaignWorkspaceError = payload;
            });
        case types.WORKSPACE_SUBMISSION_REVISION_RECEIVED:
            return produce(state, draftState => {
                if (!draftState.campaignWorkspace) return;
                const subs = draftState.campaignWorkspace.participant.content_submissions;
                if (!subs) return;
                const idx = subs.findIndex(s => s._id === payload.submissionId);
                if (idx === -1) return;
                subs[idx] = { ...subs[idx], status: 'needs_revision', feedback: payload.feedback };
            });
        case types.CLEAR_CAMPAIGN_WORKSPACE:
            return produce(state, draftState => {
                draftState.campaignWorkspace = null;
                draftState.campaignWorkspaceError = null;
                draftState.gettingCampaignWorkspace = false;
                draftState.loadingOlderWorkspaceMessages = false;
                draftState.campaignThreadMessageError = null;
                draftState.sendingCampaignThreadMessage = false;
                draftState.gettingCampaignThreadMessages = false;
                draftState.loadingOlderCampaignThreadMessages = false;
                draftState.patchingCampaignThreadMessage = false;
                draftState.deletingCampaignThreadMessage = false;
                draftState.campaignThreadTyping = {};
                draftState.campaignThreadTypingAt = {};
            });

        case types.SEND_CAMPAIGN_THREAD_MESSAGE:
            return produce(state, draftState => {
                draftState.sendingCampaignThreadMessage = true;
                draftState.campaignThreadMessageError = null;
            });
        case types.SEND_CAMPAIGN_THREAD_MESSAGE_SUCCESS:
            return produce(state, draftState => {
                draftState.sendingCampaignThreadMessage = false;
                draftState.campaignThreadMessageError = null;
                const { participantId, message } = payload;
                const ws = draftState.campaignWorkspace;
                if (!ws || String(ws.participant._id) !== String(participantId)) return;
                upsertWorkspaceThreadMessage(ws, message);
            });
        case types.SEND_CAMPAIGN_THREAD_MESSAGE_FAIL:
            return produce(state, draftState => {
                draftState.sendingCampaignThreadMessage = false;
                draftState.campaignThreadMessageError = payload;
            });

        case types.LIST_CAMPAIGN_THREAD_MESSAGES:
            return produce(state, draftState => {
                draftState.campaignThreadMessageError = null;
                if (payload.append) draftState.loadingOlderCampaignThreadMessages = true;
                else draftState.gettingCampaignThreadMessages = true;
            });
        case types.LIST_CAMPAIGN_THREAD_MESSAGES_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingCampaignThreadMessages = false;
                draftState.loadingOlderCampaignThreadMessages = false;
                draftState.campaignThreadMessageError = null;
                const { participantId, messages, messages_pagination, append } = payload;
                const ws = draftState.campaignWorkspace;
                if (!ws || String(ws.participant._id) !== String(participantId)) return;
                const normalized = messages.map(threadMessageToWorkspaceMessage);
                if (append) {
                    const existing = ws.messages ?? [];
                    const seen = new Set(existing.map(m => m._id).filter(Boolean) as string[]);
                    const prepended = normalized.filter((m: IWorkspaceMessage) => {
                        const id = m._id;
                        return !id || !seen.has(id);
                    });
                    ws.messages = [...prepended, ...existing];
                    ws.messages_pagination = messages_pagination;
                } else {
                    ws.messages = normalized;
                    ws.messages_pagination = messages_pagination;
                }
            });
        case types.LIST_CAMPAIGN_THREAD_MESSAGES_FAIL:
            return produce(state, draftState => {
                draftState.gettingCampaignThreadMessages = false;
                draftState.loadingOlderCampaignThreadMessages = false;
                draftState.campaignThreadMessageError = payload;
            });

        case types.PATCH_CAMPAIGN_THREAD_MESSAGE:
            return produce(state, draftState => {
                draftState.patchingCampaignThreadMessage = true;
                draftState.campaignThreadMessageError = null;
            });
        case types.PATCH_CAMPAIGN_THREAD_MESSAGE_SUCCESS:
            return produce(state, draftState => {
                draftState.patchingCampaignThreadMessage = false;
                draftState.campaignThreadMessageError = null;
                const { participantId, message } = payload;
                const ws = draftState.campaignWorkspace;
                if (!ws || String(ws.participant._id) !== String(participantId)) return;
                const normalized = threadMessageToWorkspaceMessage(message);
                const idx = ws.messages.findIndex(m => m._id === normalized._id);
                if (idx >= 0) ws.messages[idx] = normalized;
            });
        case types.PATCH_CAMPAIGN_THREAD_MESSAGE_FAIL:
            return produce(state, draftState => {
                draftState.patchingCampaignThreadMessage = false;
                draftState.campaignThreadMessageError = payload;
            });

        case types.DELETE_CAMPAIGN_THREAD_MESSAGE:
            return produce(state, draftState => {
                draftState.deletingCampaignThreadMessage = true;
                draftState.campaignThreadMessageError = null;
            });
        case types.DELETE_CAMPAIGN_THREAD_MESSAGE_SUCCESS:
            return produce(state, draftState => {
                draftState.deletingCampaignThreadMessage = false;
                draftState.campaignThreadMessageError = null;
                const { participantId, messageId } = payload;
                const ws = draftState.campaignWorkspace;
                if (!ws || String(ws.participant._id) !== String(participantId)) return;
                ws.messages = (ws.messages ?? []).filter(m => m._id !== messageId);
                if (ws.messages_pagination && ws.messages_pagination.total > 0) {
                    ws.messages_pagination.total -= 1;
                }
            });
        case types.DELETE_CAMPAIGN_THREAD_MESSAGE_FAIL:
            return produce(state, draftState => {
                draftState.deletingCampaignThreadMessage = false;
                draftState.campaignThreadMessageError = payload;
            });

        case types.MARK_CAMPAIGN_THREAD_READ_SUCCESS:
            return produce(state, draftState => {
                const { participantId } = payload;
                if (draftState.currentCampaign) {
                    const updated = applyThreadReadToCampaign(draftState.currentCampaign, participantId);
                    if (updated) draftState.currentCampaign = updated;
                }
                const influencer = draftState.buzzProfile?.data?.influencer;
                if (influencer) {
                    const updatedInfluencer = applyThreadReadToInfluencerProfile(influencer, participantId);
                    if (updatedInfluencer && draftState.buzzProfile?.data) {
                        draftState.buzzProfile.data.influencer = updatedInfluencer;
                    }
                }
            });

        case types.MARK_ALL_CAMPAIGN_THREADS_READ_SUCCESS:
            return produce(state, draftState => {
                if (
                    draftState.currentCampaign &&
                    String(draftState.currentCampaign._id) === String(payload.campaignId)
                ) {
                    const updated = applyAllThreadsReadToCampaign(draftState.currentCampaign);
                    if (updated) draftState.currentCampaign = updated;
                }
            });

        case types.CAMPAIGN_THREAD_MESSAGE_NEW:
        case types.CAMPAIGN_THREAD_MESSAGE_UPDATED:
            return produce(state, draftState => {
                const { participantId, message } = payload;
                const ws = draftState.campaignWorkspace;
                if (!ws) return;
                const wsPid = String(ws.participant._id);
                const eventPid = String(participantId);
                if (wsPid !== eventPid) return;
                upsertWorkspaceThreadMessage(ws, message);
            });
        case types.CAMPAIGN_THREAD_MESSAGE_DELETED:
            return produce(state, draftState => {
                const { participantId, messageId } = payload;
                const ws = draftState.campaignWorkspace;
                if (!ws || String(ws.participant._id) !== String(participantId)) return;
                ws.messages = (ws.messages ?? []).filter(m => m._id !== messageId);
                if (ws.messages_pagination && ws.messages_pagination.total > 0) {
                    ws.messages_pagination.total -= 1;
                }
            });
        case types.CAMPAIGN_THREAD_ATTACHMENT_PROCESSING:
            return produce(state, draftState => {
                const ws = draftState.campaignWorkspace;
                if (!ws || String(ws.participant._id) !== String(payload.participantId)) return;
                applyAttachmentProcessingUpdate(ws, payload);
            });
        case types.CAMPAIGN_THREAD_UNREAD_UPDATED:
            return produce(state, draftState => {
                const { participantId, unread_count, thread_role } = payload;
                if (thread_role === 'host' && draftState.currentCampaign) {
                    const updated = setParticipantUnreadOnCampaign(
                        draftState.currentCampaign,
                        participantId,
                        unread_count,
                    );
                    if (updated) draftState.currentCampaign = updated;
                }
                if (thread_role === 'influencer') {
                    const influencer = draftState.buzzProfile?.data?.influencer;
                    if (influencer) {
                        const updatedInfluencer = setParticipantUnreadOnInfluencerProfile(
                            influencer,
                            participantId,
                            unread_count,
                        );
                        if (updatedInfluencer && draftState.buzzProfile?.data) {
                            draftState.buzzProfile.data.influencer = updatedInfluencer;
                        }
                    }
                }
            });
        case types.CAMPAIGN_THREAD_TYPING_RECEIVED:
            return produce(state, draftState => {
                const key = String(payload.participantId);
                if (!draftState.campaignThreadTyping) {
                    draftState.campaignThreadTyping = {};
                }
                if (!draftState.campaignThreadTypingAt) {
                    draftState.campaignThreadTypingAt = {};
                }
                draftState.campaignThreadTyping[key] = payload.typing;
                if (payload.typing) {
                    draftState.campaignThreadTypingAt[key] = Date.now();
                } else {
                    delete draftState.campaignThreadTypingAt[key];
                }
            });
        case types.CAMPAIGN_THREAD_SOCKET_JOIN:
            return produce(state, draftState => {
                const pid = payload?.participantId?.trim();
                if (!pid) return;
                if (!draftState.campaignThreadActiveRooms) {
                    draftState.campaignThreadActiveRooms = {};
                }
                draftState.campaignThreadActiveRooms[pid] = true;
            });
        case types.CAMPAIGN_THREAD_SOCKET_LEAVE:
            return produce(state, draftState => {
                const pid = payload?.participantId?.trim();
                if (pid && draftState.campaignThreadActiveRooms) {
                    delete draftState.campaignThreadActiveRooms[pid];
                }
            });

        case types.SUBMIT_PARTICIPANT_WORK:
            return produce(state, draftState => {
                draftState.submittingParticipantWork = true;
                draftState.participantWorkSubmitError = null;
                draftState.participantUploadProgress = 0;
            });
        case types.SET_PARTICIPANT_UPLOAD_PROGRESS:
            return produce(state, draftState => {
                draftState.participantUploadProgress = payload as number;
            });
        case types.SUBMIT_PARTICIPANT_WORK_SUCCESS:
            return produce(state, draftState => {
                draftState.submittingParticipantWork = false;
                draftState.participantWorkSubmitError = null;
                // Do NOT set progress to 100 here — S3 multipart upload hasn't started yet.
                // Progress will reach 100 when COMPLETE_MULTIPART_UPLOAD_SUCCESS fires.
            });
        case types.SUBMIT_PARTICIPANT_WORK_FAIL:
            return produce(state, draftState => {
                draftState.submittingParticipantWork = false;
                draftState.participantWorkSubmitError = payload;
                draftState.participantUploadProgress = 0;
            });
        case uploadTypes.PROGRESS_REPORT:
            // Map chunk upload progress (0–100) into the 20–99 band so the modal
            // shows live progress during the actual S3 multipart upload.
            return produce(state, draftState => {
                if (draftState.participantUploadProgress > 0 && draftState.participantUploadProgress < 100) {
                    const chunkPct =
                        typeof payload === 'object' && payload !== null && 'progress' in payload
                            ? (payload as { progress: number }).progress
                            : 0;
                    draftState.participantUploadProgress = Math.min(99, 20 + Math.round(chunkPct * 0.79));
                }
            });
        case uploadTypes.COMPLETE_MULTIPART_UPLOAD_SUCCESS:
            // S3 upload fully complete — close the progress modal
            return produce(state, draftState => {
                if (draftState.participantUploadProgress > 0 && draftState.participantUploadProgress < 100) {
                    draftState.participantUploadProgress = 100;
                }
            });
        case uploadTypes.START_UPLOAD_FAIL:
        case uploadTypes.COMPLETE_MULTIPART_UPLOAD_FAIL:
            // S3 upload failed — reset progress so the modal shows the error state
            return produce(state, draftState => {
                if (draftState.participantUploadProgress > 0 && draftState.participantUploadProgress < 100) {
                    draftState.participantUploadProgress = 0;
                }
            });

        case types.SUBMIT_LIVE_URL:
            return produce(state, draftState => {
                draftState.submittingLiveUrl = true;
                draftState.liveUrlSubmitError = null;
            });
        case types.SUBMIT_LIVE_URL_SUCCESS:
            return produce(state, draftState => {
                draftState.submittingLiveUrl = false;
            });
        case types.SUBMIT_LIVE_URL_FAIL:
            return produce(state, draftState => {
                draftState.submittingLiveUrl = false;
                draftState.liveUrlSubmitError = payload;
            });

        case types.RETRY_SUBMISSION_PROCESSING:
            return produce(state, draftState => {
                draftState.retryingSubmissionProcessing = true;
            });
        case types.RETRY_SUBMISSION_PROCESSING_SUCCESS:
        case types.RETRY_SUBMISSION_PROCESSING_FAIL:
            return produce(state, draftState => {
                draftState.retryingSubmissionProcessing = false;
            });

        case types.WITHDRAW_FROM_CAMPAIGN:
            return produce(state, draftState => {
                draftState.withdrawingFromCampaign = true;
            });
        case types.WITHDRAW_FROM_CAMPAIGN_SUCCESS:
        case types.WITHDRAW_FROM_CAMPAIGN_FAIL:
            return produce(state, draftState => {
                draftState.withdrawingFromCampaign = false;
            });

        case types.REQUEST_DELIVERY_EXTENSION:
            return produce(state, draftState => {
                draftState.requestingDeliveryExtension = true;
            });
        case types.REQUEST_DELIVERY_EXTENSION_SUCCESS:
        case types.REQUEST_DELIVERY_EXTENSION_FAIL:
            return produce(state, draftState => {
                draftState.requestingDeliveryExtension = false;
            });

        case types.APPROVE_DELIVERY_EXTENSION:
        case types.REJECT_DELIVERY_EXTENSION:
            return produce(state, draftState => {
                draftState.reviewingDeliveryExtension = true;
            });
        case types.APPROVE_DELIVERY_EXTENSION_SUCCESS:
        case types.APPROVE_DELIVERY_EXTENSION_FAIL:
        case types.REJECT_DELIVERY_EXTENSION_SUCCESS:
        case types.REJECT_DELIVERY_EXTENSION_FAIL:
            return produce(state, draftState => {
                draftState.reviewingDeliveryExtension = false;
            });

        case types.OPEN_LIVE_LINK_WINDOW:
            return produce(state, draftState => {
                draftState.openingLiveLinkWindow = true;
            });
        case types.OPEN_LIVE_LINK_WINDOW_SUCCESS:
        case types.OPEN_LIVE_LINK_WINDOW_FAIL:
            return produce(state, draftState => {
                draftState.openingLiveLinkWindow = false;
            });

        case types.MARK_SHARED:
            return produce(state, draftState => {
                draftState.markingShared = true;
                draftState.markSharedError = null;
            });
        case types.MARK_SHARED_SUCCESS:
            return produce(state, draftState => {
                draftState.markingShared = false;
            });
        case types.MARK_SHARED_FAIL:
            return produce(state, draftState => {
                draftState.markingShared = false;
                draftState.markSharedError = payload;
            });

        case types.REQUEST_LIVE_URL_REVISION:
            return produce(state, draftState => {
                draftState.requestingLiveUrlRevision = true;
                draftState.liveUrlRevisionRequestError = null;
            });
        case types.REQUEST_LIVE_URL_REVISION_SUCCESS:
            return produce(state, draftState => {
                draftState.requestingLiveUrlRevision = false;
            });
        case types.REQUEST_LIVE_URL_REVISION_FAIL:
            return produce(state, draftState => {
                draftState.requestingLiveUrlRevision = false;
                draftState.liveUrlRevisionRequestError = payload;
            });

        case types.SAVE_BRAND_REVIEW:
            return produce(state, draftState => {
                draftState.savingBrandReview = true;
                draftState.brandReviewSaveError = null;
            });
        case types.SAVE_BRAND_REVIEW_SUCCESS:
            return produce(state, draftState => {
                draftState.savingBrandReview = false;
                draftState.brandReviewSaveError = null;
                const { review } = payload;
                const ws = draftState.campaignWorkspace;
                if (!ws?.brand_review) return;
                ws.brand_review = {
                    ...ws.brand_review,
                    eligible: false,
                    can_update: true,
                    reason: 'already_reviewed',
                    review,
                };
            });
        case types.SAVE_BRAND_REVIEW_FAIL:
            return produce(state, draftState => {
                draftState.savingBrandReview = false;
                draftState.brandReviewSaveError = payload;
            });

        case types.SAVE_INFLUENCER_REVIEW:
            return produce(state, draftState => {
                draftState.savingInfluencerReview = true;
                draftState.influencerReviewSaveError = null;
            });
        case types.SAVE_INFLUENCER_REVIEW_SUCCESS:
            return produce(state, draftState => {
                draftState.savingInfluencerReview = false;
                draftState.influencerReviewSaveError = null;
            });
        case types.SAVE_INFLUENCER_REVIEW_FAIL:
            return produce(state, draftState => {
                draftState.savingInfluencerReview = false;
                draftState.influencerReviewSaveError = payload;
            });

        case types.WORKSPACE_SUBMISSION_APPROVED_RECEIVED:
            return produce(state, draftState => {
                if (!draftState.campaignWorkspace) return;
                const subs = draftState.campaignWorkspace.participant.content_submissions;
                if (!subs) return;
                const idx = subs.findIndex(s => s._id === payload.submissionId);
                if (idx === -1) return;
                subs[idx] = { ...subs[idx], status: 'approved' };
            });

        case types.WORKSPACE_LIVE_URL_REVISION_RECEIVED:
            return produce(state, draftState => {
                if (!draftState.campaignWorkspace) return;
                const subs = draftState.campaignWorkspace.participant.content_submissions;
                if (!subs) return;
                const idx = subs.findIndex(s => s._id === payload.submissionId);
                if (idx === -1) return;
                subs[idx] = { ...subs[idx], feedback: payload.feedback };
            });

        case types.MANAGE_OFFSITE_CAMPAIGNS:
            return produce(state, draftState => {
                draftState.gettingManagedCampaigns = true;
                draftState.managedCampaignsError = null;
            });
        case types.MANAGE_OFFSITE_CAMPAIGNS_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingManagedCampaigns = false;
                draftState.managedCampaigns = payload.campaigns;
                draftState.managedCampaignsPagination = {
                    currentPage: payload.currentPage,
                    totalPages: payload.totalPages,
                    totalCount: payload.totalCount,
                };
            });
        case types.MANAGE_OFFSITE_CAMPAIGNS_FAIL:
            return produce(state, draftState => {
                draftState.gettingManagedCampaigns = false;
                draftState.managedCampaignsError = payload;
            });

        case types.UPDATE_OFFSITE_CAMPAIGN:
            return produce(state, draftState => {
                draftState.updatingCampaign = true;
                draftState.campaignError = null;
            });
        case types.UPDATE_OFFSITE_CAMPAIGN_SUCCESS:
            return produce(state, draftState => {
                draftState.updatingCampaign = false;
                const id = payload._id || payload.id;
                draftState.campaigns = draftState.campaigns.map(campaign =>
                    (campaign._id || campaign.id) === id ? payload : campaign,
                );
                if ((draftState.currentCampaign?._id || draftState.currentCampaign?.id) === id) {
                    draftState.currentCampaign = payload;
                }
            });
        case types.UPDATE_OFFSITE_CAMPAIGN_FAIL:
            return produce(state, draftState => {
                draftState.updatingCampaign = false;
                draftState.campaignError = payload;
            });

        case types.UPDATE_CAMPAIGN_STATUS:
            return produce(state, draftState => {
                draftState.updatingCampaignStatus = true;
                draftState.campaignError = null;
            });
        case types.UPDATE_CAMPAIGN_STATUS_SUCCESS:
            return produce(state, draftState => {
                draftState.updatingCampaignStatus = false;
            });
        case types.UPDATE_CAMPAIGN_STATUS_FAIL:
            return produce(state, draftState => {
                draftState.updatingCampaignStatus = false;
                draftState.campaignError = payload;
            });

        case types.DELETE_OFFSITE_CAMPAIGN:
            return produce(state, draftState => {
                draftState.deletingCampaign = true;
                draftState.campaignError = null;
            });
        case types.DELETE_OFFSITE_CAMPAIGN_SUCCESS:
            return produce(state, draftState => {
                draftState.deletingCampaign = false;
                draftState.campaigns = draftState.campaigns.filter(
                    campaign => (campaign._id || campaign.id) !== payload.campaignId,
                );
                if ((draftState.currentCampaign?._id || draftState.currentCampaign?.id) === payload.campaignId) {
                    draftState.currentCampaign = null;
                }
            });
        case types.DELETE_OFFSITE_CAMPAIGN_FAIL:
            return produce(state, draftState => {
                draftState.deletingCampaign = false;
                draftState.campaignError = payload;
            });

        // Brand management actions
        case types.GET_BRANDS:
            return produce(state, draftState => {
                draftState.gettingBrands = true;
                draftState.brandError = null;
            });
        case types.GET_BRANDS_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingBrands = false;
                draftState.brands = payload;
                if (draftState.activeBrandDashboardBrand?._id) {
                    const match = payload.find((b: IBrand) => b._id === draftState.activeBrandDashboardBrand?._id);
                    if (match) draftState.activeBrandDashboardBrand = match;
                }
            });
        case types.GET_BRANDS_FAIL:
            return produce(state, draftState => {
                draftState.gettingBrands = false;
                draftState.brandError = payload;
            });

        case types.GET_BRAND_SUCCESS:
            return produce(state, draftState => {
                const idx = draftState.brands.findIndex((b: IBrand) => b._id === payload._id);
                if (idx >= 0) draftState.brands[idx] = payload;
                if (draftState.activeBrandDashboardBrand?._id === payload._id) {
                    draftState.activeBrandDashboardBrand = payload;
                }
            });

        case types.SET_BRAND_DASHBOARD_ACTIVE_BRAND:
            return produce(state, draftState => {
                draftState.activeBrandDashboardBrand = payload;
            });

        case types.ADD_BRAND:
            return produce(state, draftState => {
                draftState.addingBrand = true;
                draftState.brandError = null;
            });
        case types.ADD_BRAND_SUCCESS:
            return produce(state, draftState => {
                draftState.addingBrand = false;
                const added = payload;
                draftState.brands = [
                    ...draftState.brands.map(b => (added?.is_default ? { ...b, is_default: false } : b)),
                    added,
                ];
                draftState.activeBrandDashboardBrand = added;
            });
        case types.ADD_BRAND_FAIL:
            return produce(state, draftState => {
                draftState.addingBrand = false;
                draftState.brandError = payload;
            });

        case types.UPDATE_BRAND:
            return produce(state, draftState => {
                draftState.updatingBrand = true;
                draftState.brandError = null;
            });
        case types.UPDATE_BRAND_SUCCESS:
            return produce(state, draftState => {
                draftState.updatingBrand = false;
                draftState.brands = draftState.brands.map(brand => {
                    if (brand._id === payload._id) return payload;
                    if (payload?.is_default) return { ...brand, is_default: false };
                    return brand;
                });
                if (draftState.activeBrandDashboardBrand?._id === payload._id) {
                    draftState.activeBrandDashboardBrand = payload;
                }
            });
        case types.UPDATE_BRAND_FAIL:
            return produce(state, draftState => {
                draftState.updatingBrand = false;
                draftState.brandError = payload;
            });

        case types.DELETE_BRAND:
            return produce(state, draftState => {
                draftState.deletingBrand = true;
                draftState.brandError = null;
            });
        case types.DELETE_BRAND_SUCCESS:
            return produce(state, draftState => {
                draftState.deletingBrand = false;
                const wasActive = draftState.activeBrandDashboardBrand?._id === payload.documentId;
                draftState.brands = draftState.brands.filter(brand => brand._id !== payload.documentId);
                if (wasActive) {
                    draftState.activeBrandDashboardBrand = draftState.brands[0] ?? null;
                }
            });
        case types.DELETE_BRAND_FAIL:
            return produce(state, draftState => {
                draftState.deletingBrand = false;
                draftState.brandError = payload;
            });

        // Host dashboard actions
        case types.GET_HOST_DASHBOARD:
            return produce(state, draftState => {
                draftState.gettingHostDashboard = true;
                draftState.hostDashboardError = null;
            });
        case types.GET_HOST_DASHBOARD_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingHostDashboard = false;
                draftState.hostDashboard = payload;
                draftState.brands = payload.brands ?? [];
                const ownedBrands: any[] = payload.brands ?? [];
                if (ownedBrands.length > 0) {
                    // Brand owner: resolve from their owned brands. Fall back to
                    // brands[0] if neither active_brand nor default_brand is set.
                    draftState.activeBrandDashboardBrand =
                        payload.active_brand ?? payload.default_brand ?? ownedBrands[0] ?? null;
                }
                // If brands is empty the user owns no brand — they may be an
                // invited manager whose brand was set via setBrandDashboardActiveBrand.
                // Do NOT override it here.
            });
        case types.GET_HOST_DASHBOARD_FAIL:
            return produce(state, draftState => {
                draftState.gettingHostDashboard = false;
                draftState.hostDashboardError = payload;
            });

        // Join request actions
        case types.REQUEST_TO_JOIN_CAMPAIGN:
            return produce(state, draftState => {
                draftState.requestingToJoin = true;
                draftState.joinRequestError = null;
            });
        case types.REQUEST_TO_JOIN_CAMPAIGN_SUCCESS:
            return produce(state, draftState => {
                draftState.requestingToJoin = false;
                draftState.joinRequests = [...draftState.joinRequests, payload];
            });
        case types.REQUEST_TO_JOIN_CAMPAIGN_FAIL:
            return produce(state, draftState => {
                draftState.requestingToJoin = false;
                draftState.joinRequestError = payload;
            });

        case types.APPROVE_CAMPAIGN_JOIN_REQUEST:
            return produce(state, draftState => {
                draftState.approvingRequest = true;
                draftState.joinRequestError = null;
            });
        case types.APPROVE_CAMPAIGN_JOIN_REQUEST_SUCCESS:
            return produce(state, draftState => {
                draftState.approvingRequest = false;
                draftState.joinRequests = draftState.joinRequests.map(request =>
                    request.id === payload.requestId ? { ...request, status: 'approved' } : request,
                );
            });
        case types.APPROVE_CAMPAIGN_JOIN_REQUEST_FAIL:
            return produce(state, draftState => {
                draftState.approvingRequest = false;
                draftState.joinRequestError = payload;
            });

        case types.BULK_APPROVE_CAMPAIGN_JOIN_REQUESTS:
            return produce(state, draftState => {
                draftState.bulkApprovingRequests = true;
                draftState.joinRequestError = null;
            });
        case types.BULK_APPROVE_CAMPAIGN_JOIN_REQUESTS_SUCCESS:
            return produce(state, draftState => {
                draftState.bulkApprovingRequests = false;
            });
        case types.BULK_APPROVE_CAMPAIGN_JOIN_REQUESTS_FAIL:
            return produce(state, draftState => {
                draftState.bulkApprovingRequests = false;
                draftState.joinRequestError = payload;
            });

        case types.BULK_REJECT_CAMPAIGN_JOIN_REQUESTS:
            return produce(state, draftState => {
                draftState.bulkRejectingRequests = true;
                draftState.joinRequestError = null;
            });
        case types.BULK_REJECT_CAMPAIGN_JOIN_REQUESTS_SUCCESS:
            return produce(state, draftState => {
                draftState.bulkRejectingRequests = false;
            });
        case types.BULK_REJECT_CAMPAIGN_JOIN_REQUESTS_FAIL:
            return produce(state, draftState => {
                draftState.bulkRejectingRequests = false;
                draftState.joinRequestError = payload;
            });

        case types.BULK_RESCIND_CAMPAIGN_PARTICIPANTS:
            return produce(state, draftState => {
                draftState.bulkRescindingParticipants = true;
                draftState.joinRequestError = null;
            });
        case types.BULK_RESCIND_CAMPAIGN_PARTICIPANTS_SUCCESS:
            return produce(state, draftState => {
                draftState.bulkRescindingParticipants = false;
                draftState.bulkRescindLastSkipped = payload?.skipped_details ?? [];
            });
        case types.BULK_RESCIND_CAMPAIGN_PARTICIPANTS_FAIL:
            return produce(state, draftState => {
                draftState.bulkRescindingParticipants = false;
                draftState.joinRequestError = payload;
            });

        case types.REJECT_CAMPAIGN_JOIN_REQUEST:
            return produce(state, draftState => {
                draftState.rejectingRequest = true;
                draftState.joinRequestError = null;
            });
        case types.REJECT_CAMPAIGN_JOIN_REQUEST_SUCCESS:
            return produce(state, draftState => {
                draftState.rejectingRequest = false;
                draftState.joinRequests = draftState.joinRequests.map(request =>
                    request.id === payload.requestId ? { ...request, status: 'rejected' } : request,
                );
            });
        case types.REJECT_CAMPAIGN_JOIN_REQUEST_FAIL:
            return produce(state, draftState => {
                draftState.rejectingRequest = false;
                draftState.joinRequestError = payload;
            });

        case types.GET_CAMPAIGN_PENDING_INVITES:
            return produce(state, draftState => {
                draftState.gettingPendingInvites = true;
                draftState.joinRequestError = null;
            });
        case types.GET_CAMPAIGN_PENDING_INVITES_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingPendingInvites = false;
                draftState.pendingInvitesList = payload.invites ?? [];
                draftState.pendingInvitesPagination = payload.pagination ?? {
                    page: 1,
                    limit: 20,
                    total: 0,
                    totalPages: 1,
                    hasMore: false,
                };
            });
        case types.GET_CAMPAIGN_PENDING_INVITES_FAIL:
            return produce(state, draftState => {
                draftState.gettingPendingInvites = false;
                draftState.joinRequestError = payload;
            });

        case types.CANCEL_CAMPAIGN_INVITE:
            return produce(state, draftState => {
                draftState.cancellingInvite = true;
                draftState.joinRequestError = null;
            });
        case types.CANCEL_CAMPAIGN_INVITE_SUCCESS:
            return produce(state, draftState => {
                draftState.cancellingInvite = false;
                const participantId = payload.participantId as string;
                draftState.pendingInvitesList = draftState.pendingInvitesList.filter(
                    (i: ICampaignParticipantDetail) => i._id !== participantId,
                );
                if (draftState.currentCampaign?.pending_invites) {
                    draftState.currentCampaign.pending_invites = draftState.currentCampaign.pending_invites.filter(
                        (i: ICampaignParticipantDetail) => i._id !== participantId,
                    );
                }
                if (draftState.pendingInvitesPagination.total > 0) {
                    draftState.pendingInvitesPagination.total -= 1;
                }
            });
        case types.CANCEL_CAMPAIGN_INVITE_FAIL:
            return produce(state, draftState => {
                draftState.cancellingInvite = false;
                draftState.joinRequestError = payload;
            });

        case types.RESCIND_CAMPAIGN_PARTICIPANT_APPROVAL:
            return produce(state, draftState => {
                draftState.rejectingRequest = true;
                draftState.joinRequestError = null;
            });
        case types.RESCIND_CAMPAIGN_PARTICIPANT_APPROVAL_SUCCESS:
            return produce(state, draftState => {
                draftState.rejectingRequest = false;
                const { participantId, participants_count } = payload as {
                    participantId: string;
                    participants_count?: number;
                };
                const cur = draftState.currentCampaign;
                if (cur) {
                    const nextParticipants = (cur.participants ?? []).filter(
                        (p: { _id?: string; id?: string }) => String(p._id ?? p.id) !== String(participantId),
                    );
                    const rejected = (cur.participants ?? []).find(
                        (p: { _id?: string; id?: string }) => String(p._id ?? p.id) === String(participantId),
                    );
                    draftState.currentCampaign = {
                        ...cur,
                        participants: nextParticipants,
                        participants_count:
                            typeof participants_count === 'number' ? participants_count : nextParticipants.length,
                        rejected_participants: rejected
                            ? [{ ...rejected, status: 'rejected' }, ...(cur.rejected_participants ?? [])]
                            : cur.rejected_participants,
                    };
                }
            });
        case types.RESCIND_CAMPAIGN_PARTICIPANT_APPROVAL_FAIL:
            return produce(state, draftState => {
                draftState.rejectingRequest = false;
                draftState.joinRequestError = payload;
            });

        // Message actions
        case types.SEND_CAMPAIGN_MESSAGE:
            return produce(state, draftState => {
                draftState.sendingMessage = true;
                draftState.messageError = null;
            });
        case types.SEND_CAMPAIGN_MESSAGE_SUCCESS:
            return produce(state, draftState => {
                draftState.sendingMessage = false;
                draftState.messages = [...draftState.messages, payload];
            });
        case types.SEND_CAMPAIGN_MESSAGE_FAIL:
            return produce(state, draftState => {
                draftState.sendingMessage = false;
                draftState.messageError = payload;
            });

        case types.DELETE_CAMPAIGN_MESSAGE:
            return produce(state, draftState => {
                draftState.deletingMessage = true;
                draftState.messageError = null;
            });
        case types.DELETE_CAMPAIGN_MESSAGE_SUCCESS:
            return produce(state, draftState => {
                draftState.deletingMessage = false;
                draftState.messages = draftState.messages.filter(message => message.id !== payload.messageId);
            });
        case types.DELETE_CAMPAIGN_MESSAGE_FAIL:
            return produce(state, draftState => {
                draftState.deletingMessage = false;
                draftState.messageError = payload;
            });

        case types.UPDATE_CAMPAIGN_MESSAGE:
            return produce(state, draftState => {
                draftState.updatingMessage = true;
                draftState.messageError = null;
            });
        case types.UPDATE_CAMPAIGN_MESSAGE_SUCCESS:
            return produce(state, draftState => {
                draftState.updatingMessage = false;
                draftState.messages = draftState.messages.map(message =>
                    message.id === payload.id ? payload : message,
                );
            });
        case types.UPDATE_CAMPAIGN_MESSAGE_FAIL:
            return produce(state, draftState => {
                draftState.updatingMessage = false;
                draftState.messageError = payload;
            });

        // Deliverable actions
        case types.REQUEST_DELIEVERABLE_UPDATE:
            return produce(state, draftState => {
                draftState.requestingUpdate = true;
                draftState.deliverableError = null;
            });
        case types.REQUEST_DELIEVERABLE_UPDATE_SUCCESS:
            return produce(state, draftState => {
                draftState.requestingUpdate = false;
                draftState.deliverables = [...draftState.deliverables, payload];
            });
        case types.REQUEST_DELIEVERABLE_UPDATE_FAIL:
            return produce(state, draftState => {
                draftState.requestingUpdate = false;
                draftState.deliverableError = payload;
            });

        case types.BULK_REQUEST_REVISION:
            return produce(state, draftState => {
                draftState.bulkRequestingRevision = true;
                draftState.deliverableError = null;
            });
        case types.BULK_REQUEST_REVISION_SUCCESS:
            return produce(state, draftState => {
                draftState.bulkRequestingRevision = false;
            });
        case types.BULK_REQUEST_REVISION_FAIL:
            return produce(state, draftState => {
                draftState.bulkRequestingRevision = false;
                draftState.deliverableError = payload;
            });

        case types.RELAUNCH_CAMPAIGN:
            return produce(state, draftState => {
                draftState.relaunchingCampaign = true;
                draftState.relaunchedCampaignId = null;
            });
        case types.RELAUNCH_CAMPAIGN_SUCCESS:
            return produce(state, draftState => {
                draftState.relaunchingCampaign = false;
                draftState.relaunchedCampaignId = payload?._id ?? null;
            });
        case types.RELAUNCH_CAMPAIGN_FAIL:
            return produce(state, draftState => {
                draftState.relaunchingCampaign = false;
            });

        case types.APPROVE_DELIVERABLE_SUBMISSION:
            return produce(state, draftState => {
                draftState.approvingDeliverable = true;
                draftState.deliverableError = null;
            });
        case types.APPROVE_DELIVERABLE_SUBMISSION_SUCCESS:
            return produce(state, draftState => {
                draftState.approvingDeliverable = false;
            });
        case types.APPROVE_DELIVERABLE_SUBMISSION_FAIL:
            return produce(state, draftState => {
                draftState.approvingDeliverable = false;
                draftState.deliverableError = payload;
            });

        case types.UPLOAD_DELIVERABLE:
            return produce(state, draftState => {
                draftState.uploadingDeliverable = true;
                draftState.deliverableError = null;
            });
        case types.UPLOAD_DELIVERABLE_SUCCESS:
            return produce(state, draftState => {
                draftState.uploadingDeliverable = false;
                draftState.deliverables = [...draftState.deliverables, payload];
            });
        case types.UPLOAD_DELIVERABLE_FAIL:
            return produce(state, draftState => {
                draftState.uploadingDeliverable = false;
                draftState.deliverableError = payload;
            });

        // Post scheduling actions
        case types.SCHEDULE_POST:
            return produce(state, draftState => {
                draftState.schedulingPost = true;
                draftState.scheduleError = null;
            });
        case types.SCHEDULE_POST_SUCCESS:
            return produce(state, draftState => {
                draftState.schedulingPost = false;
                draftState.scheduledPosts = [...draftState.scheduledPosts, payload];
            });
        case types.SCHEDULE_POST_FAIL:
            return produce(state, draftState => {
                draftState.schedulingPost = false;
                draftState.scheduleError = payload;
            });
        case types.GET_BUZZBRIDGE_PROFILE:
            return produce(state, draftState => {
                if (!payload.skipRefresh) {
                    draftState.gettingBuzzProfile = true;
                    draftState.noInfluencerProfile = false;
                }
            });
        case types.GET_BUZZBRIDGE_PROFILE_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingBuzzProfile = false;
                draftState.buzzProfile = payload;
                draftState.noInfluencerProfile = false;
            });
        case types.GET_BUZZBRIDGE_PROFILE_NO_INFLUENCER:
            return produce(state, draftState => {
                draftState.gettingBuzzProfile = false;
                draftState.buzzProfile = null;
                draftState.noInfluencerProfile = true;
            });
        case types.GET_BUZZBRIDGE_PROFILE_FAIL:
            return produce(state, draftState => {
                draftState.gettingBuzzProfile = false;
                draftState.error = payload;
            });
        case types.GET_PUBLIC_INFLUENCER_PROFILE:
            return produce(state, draftState => {
                draftState.gettingPublicInfluencerProfile = true;
                draftState.publicInfluencerProfileUserName = payload.userName.replace(/^@/, '');
                draftState.publicInfluencerProfileError = null;
            });
        case types.GET_PUBLIC_INFLUENCER_PROFILE_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingPublicInfluencerProfile = false;
                draftState.publicInfluencerProfile = payload;
            });
        case types.GET_PUBLIC_INFLUENCER_PROFILE_FAIL:
            return produce(state, draftState => {
                draftState.gettingPublicInfluencerProfile = false;
                draftState.publicInfluencerProfile = null;
                draftState.publicInfluencerProfileError = payload;
            });
        case types.UPDATE_BUZZBRIDGE_PROFILE_SUCCESS:
            return produce(state, draftState => {
                const { updatedFields, ...rest } = payload as IInfluencer & { updatedFields?: string[] };
                const inf = draftState.buzzProfile?.data?.influencer;

                /** First manual link / create — bootstrap profile so the next platform PATCHes. */
                if (!inf) {
                    if (!rest._id) return;
                    draftState.noInfluencerProfile = false;
                    const existingProfile = draftState.buzzProfile;
                    if (existingProfile?.data) {
                        existingProfile.data.influencer = rest as IInfluencer;
                    } else if (existingProfile) {
                        existingProfile.data = { influencer: rest as IInfluencer };
                    } else {
                        draftState.buzzProfile = {
                            status: 'success',
                            pageSize: 0,
                            currentPage: 1,
                            data: { influencer: rest as IInfluencer },
                            totalCount: {},
                            totalPages: {},
                            hasNextPage: {},
                            hasPreviousPage: {},
                            nextPage: {},
                            previousPage: {},
                            results: {},
                        } as IBuzzBridgeProfileResponse;
                    }
                    return;
                }

                /** PATCH returns influencer doc; preserve paginated job lists from GET /me/profile. */
                const PROFILE_LIST_KEYS = [
                    'active_jobs',
                    'requests',
                    'invitations',
                    'completed_jobs',
                    'active_jobs_count',
                    'requests_count',
                    'invitations_count',
                    'completed_jobs_count',
                    'pending_applications_count',
                    'approved_applications_count',
                    'total_earned_cents',
                    'monthly_earned_cents',
                    'monthly_goal_progress_percent',
                ] as const;

                if (rest._id) {
                    const preserved: Partial<IInfluencer> = {};
                    PROFILE_LIST_KEYS.forEach(key => {
                        const value = inf[key];
                        if (value !== undefined) {
                            (preserved as Record<string, unknown>)[key] = value;
                        }
                    });
                    draftState.buzzProfile = {
                        ...draftState.buzzProfile!,
                        data: {
                            ...draftState.buzzProfile!.data,
                            influencer: {
                                ...inf,
                                ...rest,
                                ...preserved,
                            } as IInfluencer,
                        },
                    };
                    return;
                }

                Object.keys(rest).forEach(key => {
                    const typedKey = key as keyof IInfluencer;
                    if (updatedFields?.includes(typedKey)) {
                        draftState.buzzProfile = {
                            ...draftState.buzzProfile!,
                            data: {
                                ...draftState.buzzProfile!.data,
                                influencer: {
                                    ...draftState.buzzProfile!.data.influencer,
                                    [typedKey]: rest[typedKey],
                                },
                            },
                        };
                    }
                });
            });
        // Brand social OAuth
        case types.BRAND_SOCIAL_LOGIN:
            return produce(state, draftState => {
                draftState.connectingBrandSocial = true;
            });
        case types.BRAND_SOCIAL_LOGIN_SUCCESS:
            return produce(state, draftState => {
                draftState.connectingBrandSocial = false;
            });
        case types.BRAND_SOCIAL_LOGIN_FAIL:
            return produce(state, draftState => {
                draftState.connectingBrandSocial = false;
            });

        // Campaign Wallet
        case types.GET_WALLET_DASHBOARD:
            return produce(state, d => {
                d.gettingWalletDashboard = true;
                d.walletError = null;
            });
        case types.GET_WALLET_DASHBOARD_SUCCESS:
            return produce(state, d => {
                d.gettingWalletDashboard = false;
                d.walletDashboard = payload;
            });
        case types.GET_WALLET_DASHBOARD_FAIL:
            return produce(state, d => {
                d.gettingWalletDashboard = false;
                d.walletError = payload;
            });

        case types.GET_COMMISSION_WALLET:
            return produce(state, d => {
                d.gettingCommissionWallet = true;
                d.commissionWalletError = null;
            });
        case types.GET_COMMISSION_WALLET_SUCCESS:
            return produce(state, d => {
                d.gettingCommissionWallet = false;
                d.commissionWallet = payload;
            });
        case types.GET_COMMISSION_WALLET_FAIL:
            return produce(state, d => {
                d.gettingCommissionWallet = false;
                d.commissionWalletError = payload;
            });

        case types.CASH_OUT_COMMISSION_WALLET:
            return produce(state, d => {
                d.cashingOutCommissionWallet = true;
                d.commissionWalletError = null;
            });
        case types.CASH_OUT_COMMISSION_WALLET_SUCCESS:
            return produce(state, d => {
                d.cashingOutCommissionWallet = false;
                d.commissionWallet = {
                    ...(d.commissionWallet || {
                        earnings: [],
                        enrollments: [],
                        other_balance_cents: 0,
                        total_cents: 0,
                        total_available_cents: 0,
                        commission: {
                            pending_cents: 0,
                            balance_cents: 0,
                            lifetime_earned_cents: 0,
                            lifetime_cleared_cents: 0,
                        },
                    }),
                    ...payload,
                };
            });
        case types.CASH_OUT_COMMISSION_WALLET_FAIL:
            return produce(state, d => {
                d.cashingOutCommissionWallet = false;
                d.commissionWalletError = payload;
            });

        case types.GET_WALLET_FUND_OPTIONS:
            return produce(state, d => {
                d.gettingWalletFundOptions = true;
                d.walletError = null;
            });
        case types.GET_WALLET_FUND_OPTIONS_SUCCESS:
            return produce(state, d => {
                d.gettingWalletFundOptions = false;
                d.walletFundOptions = payload;
            });
        case types.GET_WALLET_FUND_OPTIONS_FAIL:
            return produce(state, d => {
                d.gettingWalletFundOptions = false;
                d.walletError = payload;
            });

        case types.FUND_WALLET:
            return produce(state, d => {
                d.fundingWallet = true;
                d.walletError = null;
            });
        case types.FUND_WALLET_SUCCESS:
            return produce(state, d => {
                d.fundingWallet = false;
            });
        case types.FUND_WALLET_FAIL:
            return produce(state, d => {
                d.fundingWallet = false;
                d.walletError = payload;
            });

        case types.LOCK_ESCROW:
        case types.RELEASE_ESCROW:
            return produce(state, d => {
                d.walletError = null;
            });
        case types.PAYOUT_ESCROW:
            return produce(state, d => {
                d.payingOut = true;
                d.payoutError = null;
                d.walletError = null;
            });
        case types.LOCK_ESCROW_SUCCESS:
            return produce(state, d => {
                const escrow = payload as IEscrow;
                const cid = escrow?.campaign;
                if (cid == null) return;
                const idStr = String(cid);
                const cur = d.currentCampaign;
                if (cur && String(cur._id || cur.id) === idStr) {
                    d.currentCampaign = { ...cur, funded: true };
                }
                d.campaigns = d.campaigns.map((c: { _id?: string; id?: string }) =>
                    String(c._id || c.id) === idStr ? { ...c, funded: true } : c,
                );
            });
        case types.RELEASE_ESCROW_SUCCESS:
            return state;
        case types.PAYOUT_ESCROW_SUCCESS:
            return produce(state, d => {
                d.payingOut = false;
                d.payoutError = null;
            });
        case types.LOCK_ESCROW_FAIL:
        case types.RELEASE_ESCROW_FAIL:
            return produce(state, d => {
                d.walletError = payload;
            });
        case types.PAYOUT_ESCROW_FAIL:
            return produce(state, d => {
                d.payingOut = false;
                d.payoutError = payload;
                d.walletError = payload;
            });

        case types.PAYOUT_ESCROW_ROUND:
            return produce(state, d => {
                d.payingOutRound = true;
            });
        case types.PAYOUT_ESCROW_ROUND_SUCCESS:
            return produce(state, d => {
                d.payingOutRound = false;
            });
        case types.PAYOUT_ESCROW_ROUND_FAIL:
            return produce(state, d => {
                d.payingOutRound = false;
            });

        case types.UPDATE_AUTO_RELOAD_SUCCESS:
            return produce(state, d => {
                if (d.walletDashboard && payload.auto_reload) {
                    const ar = payload.auto_reload;
                    // no direct wallet object on dashboard; kept for future reference
                    void ar;
                }
            });
        case types.UPDATE_AUTO_RELOAD_FAIL:
            return produce(state, d => {
                d.walletError = payload;
            });

        case types.GET_WALLET_TRANSACTIONS:
            return produce(state, d => {
                d.gettingWalletTransactions = true;
            });
        case types.GET_WALLET_TRANSACTIONS_SUCCESS:
            return produce(state, d => {
                d.gettingWalletTransactions = false;
                d.walletTransactions = payload;
            });
        case types.GET_WALLET_TRANSACTIONS_FAIL:
            return produce(state, d => {
                d.gettingWalletTransactions = false;
                d.walletError = payload;
            });

        case types.ADMIN_CREDIT_CAMPAIGN_WALLET:
            return produce(state, d => {
                d.adminCreditingCampaignWallet = true;
                d.adminCampaignWalletCreditResult = null;
                d.walletError = null;
            });
        case types.ADMIN_CREDIT_CAMPAIGN_WALLET_SUCCESS:
            return produce(state, d => {
                d.adminCreditingCampaignWallet = false;
                d.adminCampaignWalletCreditResult = payload;
            });
        case types.ADMIN_CREDIT_CAMPAIGN_WALLET_FAIL:
            return produce(state, d => {
                d.adminCreditingCampaignWallet = false;
                d.walletError = payload;
            });
        case types.CLEAR_ADMIN_CAMPAIGN_WALLET_CREDIT:
            return produce(state, d => {
                d.adminCampaignWalletCreditResult = null;
            });

        case types.GET_CAMPAIGN_REPORT:
            return produce(state, d => {
                d.gettingCampaignReport = true;
                d.campaignReportError = null;
            });
        case types.GET_CAMPAIGN_REPORT_SUCCESS:
            return produce(state, d => {
                d.gettingCampaignReport = false;
                d.campaignReportSummary = payload;
            });
        case types.GET_CAMPAIGN_REPORT_FAIL:
            return produce(state, d => {
                d.gettingCampaignReport = false;
                d.campaignReportError = payload;
            });

        case types.GET_CAMPAIGN_REPORT_CONTENT:
            return produce(state, d => {
                d.gettingCampaignReportContent = true;
                d.campaignReportError = null;
            });
        case types.GET_CAMPAIGN_REPORT_CONTENT_SUCCESS:
            return produce(state, d => {
                d.gettingCampaignReportContent = false;
                d.campaignReportContent = payload;
            });
        case types.GET_CAMPAIGN_REPORT_CONTENT_FAIL:
            return produce(state, d => {
                d.gettingCampaignReportContent = false;
                d.campaignReportError = payload;
            });

        case types.GET_CAMPAIGN_REPORT_CREATORS:
            return produce(state, d => {
                d.gettingCampaignReportCreators = true;
                d.campaignReportError = null;
            });
        case types.GET_CAMPAIGN_REPORT_CREATORS_SUCCESS:
            return produce(state, d => {
                d.gettingCampaignReportCreators = false;
                d.campaignReportCreators = payload;
            });
        case types.GET_CAMPAIGN_REPORT_CREATORS_FAIL:
            return produce(state, d => {
                d.gettingCampaignReportCreators = false;
                d.campaignReportError = payload;
            });

        case types.GET_CAMPAIGN_REPORT_SYNC_STATUS:
            return produce(state, d => {
                d.gettingCampaignReportSyncStatus = true;
            });
        case types.GET_CAMPAIGN_REPORT_SYNC_STATUS_SUCCESS:
            return produce(state, d => {
                d.gettingCampaignReportSyncStatus = false;
                d.campaignReportSyncStatus = payload;
            });
        case types.GET_CAMPAIGN_REPORT_SYNC_STATUS_FAIL:
            return produce(state, d => {
                d.gettingCampaignReportSyncStatus = false;
                d.campaignReportError = payload;
            });

        case types.SYNC_CAMPAIGN_REPORT:
            return produce(state, d => {
                d.syncingCampaignReport = true;
            });
        case types.SYNC_CAMPAIGN_REPORT_SUCCESS:
        case types.SYNC_CAMPAIGN_REPORT_FAIL:
            return produce(state, d => {
                d.syncingCampaignReport = false;
                if (type === types.SYNC_CAMPAIGN_REPORT_FAIL) d.campaignReportError = payload;
            });

        case types.BOOST_CAMPAIGN_REPORT:
            return produce(state, d => {
                d.boostingCampaignReport = true;
                d.campaignReportBoostResult = null;
                d.campaignReportError = null;
            });
        case types.BOOST_CAMPAIGN_REPORT_SUCCESS:
            return produce(state, d => {
                d.boostingCampaignReport = false;
                d.campaignReportBoostResult = payload;
            });
        case types.BOOST_CAMPAIGN_REPORT_FAIL:
            return produce(state, d => {
                d.boostingCampaignReport = false;
                d.campaignReportError = payload;
            });

        case types.CLEAR_CAMPAIGN_REPORT:
            return produce(state, d => {
                d.campaignReportSummary = null;
                d.campaignReportContent = null;
                d.campaignReportCreators = null;
                d.campaignReportSyncStatus = null;
                d.campaignReportBoostResult = null;
                d.boostingCampaignReport = false;
                d.campaignReportError = null;
            });

        case 'LOGIN_SUCCESS':
        case 'LOGOUT_SUCCESS':
            // Drop cached workspace/thread so a newly signed-in user never inherits
            // another account's participant messages (public campaign detail stays).
            return {
                ...initialState,
                currentCampaign: state.currentCampaign,
            };
        default:
            return state;
    }
};

export { reducer };
